import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ANCIENNETE_ALERTE_JOURS,
  IMPORT_STATUS_LABELS,
  IMPORT_TYPE_LABELS,
  type DashboardEcarts,
  type KpiSnapshot,
} from '@kn/shared';
import { api } from '../api/client';
import { messageErreur } from '../api/error-message';
import { useAuth } from '../auth/auth-context';

function nombre(valeur: number): string {
  return valeur.toLocaleString('fr-FR');
}

function pourcentage(taux: number): string {
  return `${(taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
}

function formaterHorodatage(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Tendance du KPI J+5 entre la mesure précédente et la dernière.
 *
 * Une flèche sans point de comparaison serait un ornement : tant qu'il n'y a
 * qu'une mesure, l'écran le dit plutôt que d'inventer une direction.
 */
function tendance(historique: KpiSnapshot[]): { texte: string; sens: 'hausse' | 'baisse' | 'plat' } {
  if (historique.length < 2) {
    return { texte: 'première mesure, pas encore de tendance', sens: 'plat' };
  }
  const dernier = historique.at(-1)!.auDelaJ5;
  const precedent = historique.at(-2)!.auDelaJ5;
  const delta = dernier - precedent;
  if (delta === 0) return { texte: 'stable depuis le relevé précédent', sens: 'plat' };
  return {
    texte: `${delta > 0 ? '+' : '−'}${nombre(Math.abs(delta))} depuis le relevé précédent`,
    sens: delta > 0 ? 'hausse' : 'baisse',
  };
}

/** Répartition par ancienneté, en barres proportionnelles au plus gros paquet. */
function Anciennete({ ecarts }: { ecarts: DashboardEcarts }): ReactElement {
  const maximum = Math.max(1, ...ecarts.tranches.map((t) => t.nombre));

  return (
    <ul className="barres">
      {ecarts.tranches.map((tranche) => (
        <li key={tranche.libelle}>
          <span className="barres__libelle">{tranche.libelle}</span>
          <span className="barres__piste">
            <span
              className={`barres__valeur${tranche.alerte ? ' barres__valeur--alerte' : ''}`}
              style={{ width: `${Math.round((tranche.nombre / maximum) * 100)}%` }}
            />
          </span>
          <span className="barres__nombre">{nombre(tranche.nombre)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Page d'accueil du pilotage.
 *
 * Quatre blocs, tous cliquables vers l'écran qui permet d'agir : voir un
 * chiffre sans pouvoir l'ouvrir n'aide personne à instruire quoi que ce soit.
 */
export function DashboardPage(): ReactElement {
  const { user } = useAuth();
  const tableau = useQuery({ queryKey: ['dashboard'], queryFn: () => api.dashboard() });

  return (
    <>
      <h1>Tableau de bord</h1>
      <p className="sous-titre">
        Bonjour{user ? ` ${user.displayName}` : ''} &mdash; état du rapprochement douane / accises,
        recalculé sur les données importées.
      </p>

      {tableau.isPending && <p className="chargement">Chargement du tableau de bord…</p>}
      {tableau.isError && <p className="alerte alerte--erreur">{messageErreur(tableau.error)}</p>}

      {tableau.data && (
        <>
          <div className="kpi">
            <div className="kpi__mesure kpi__mesure--principale">
              <span>Écarts au-delà de J+{ANCIENNETE_ALERTE_JOURS}</span>
              <strong>{nombre(tableau.data.article.auDelaJ5)}</strong>
              <small>{tendance(tableau.data.historique).texte}</small>
            </div>
            <div className="kpi__mesure">
              <span>Écarts ouverts (article)</span>
              <strong>{nombre(tableau.data.article.ouverts)}</strong>
              <small>reste à instruire, périmètre de contrôle</small>
            </div>
            <div className="kpi__mesure">
              <span>Entrées alignées hors ODE</span>
              <strong>{pourcentage(tableau.data.kpiEntrees.horsOde.taux)}</strong>
              <small>
                {nombre(tableau.data.kpiEntrees.horsOde.alignes)} article(s) alignés sur{' '}
                {nombre(tableau.data.kpiEntrees.horsOde.articles)}
              </small>
            </div>
            <div className="kpi__mesure">
              <span>Poids des ODE</span>
              <strong>{pourcentage(tableau.data.kpiEntrees.poidsOdePoints)}</strong>
              <small>
                {nombre(Number(tableau.data.kpiEntrees.poidsOdeUvc))} UVC déclarées par des
                mouvements manuels ; taux avec ODE :{' '}
                {pourcentage(tableau.data.kpiEntrees.avecOde.taux)}
              </small>
            </div>
          </div>

          <div className="grille-blocs">
            <section className="carte">
              <div className="barre-actions barre-actions--titre">
                <h2>Écarts par statut</h2>
                <Link className="bouton bouton--lien" to="/ecarts">
                  Ouvrir les écarts
                </Link>
              </div>
              <table className="tableau">
                <thead>
                  <tr>
                    <th scope="col">Statut</th>
                    <th scope="col">Article</th>
                    <th scope="col">Famille</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Ouverts, à instruire</th>
                    <td className="droite">{nombre(tableau.data.article.ouverts)}</td>
                    <td className="droite">{nombre(tableau.data.famille.ouverts)}</td>
                  </tr>
                  <tr>
                    <th scope="row">Couverts par une validation temporaire</th>
                    <td className="droite">{nombre(tableau.data.article.couvertsTemporaire)}</td>
                    <td className="droite">{nombre(tableau.data.famille.couvertsTemporaire)}</td>
                  </tr>
                  <tr>
                    <th scope="row">Portant une validation définitive</th>
                    <td className="droite">{nombre(tableau.data.article.valideDefinitif)}</td>
                    <td className="droite">{nombre(tableau.data.famille.valideDefinitif)}</td>
                  </tr>
                  <tr>
                    <th scope="row">Soldés, conservés en historique</th>
                    <td className="droite">{nombre(tableau.data.article.soldes)}</td>
                    <td className="droite">{nombre(tableau.data.famille.soldes)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section className="carte">
              <div className="barre-actions barre-actions--titre">
                <h2>Ancienneté des écarts ouverts</h2>
                <Link className="bouton bouton--lien" to="/ecarts">
                  Instruire les plus anciens
                </Link>
              </div>
              <p className="sous-titre">
                Maille article. Au-delà de J+{ANCIENNETE_ALERTE_JOURS}, le délai d&apos;instruction
                visé est dépassé.
              </p>
              <Anciennete ecarts={tableau.data.article} />
            </section>

            <section className="carte">
              <div className="barre-actions barre-actions--titre">
                <h2>KPI d&apos;entrées</h2>
                <Link className="bouton bouton--lien" to="/rapprochement">
                  Ouvrir le rapprochement
                </Link>
              </div>
              <p className="sous-titre">
                Réceptions GERECD confrontées aux entrées déclarées. La mesure de référence exclut
                les ODE ; leur poids est une information métier à part entière.
              </p>
              <table className="tableau">
                <thead>
                  <tr>
                    <th scope="col">Mesure</th>
                    <th scope="col">Articles</th>
                    <th scope="col">Alignés</th>
                    <th scope="col">Taux</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Hors ODE (référence)</th>
                    <td className="droite">{nombre(tableau.data.kpiEntrees.horsOde.articles)}</td>
                    <td className="droite">{nombre(tableau.data.kpiEntrees.horsOde.alignes)}</td>
                    <td className="droite">
                      <strong>{pourcentage(tableau.data.kpiEntrees.horsOde.taux)}</strong>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Avec ODE</th>
                    <td className="droite">{nombre(tableau.data.kpiEntrees.avecOde.articles)}</td>
                    <td className="droite">{nombre(tableau.data.kpiEntrees.avecOde.alignes)}</td>
                    <td className="droite">{pourcentage(tableau.data.kpiEntrees.avecOde.taux)}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section className="carte">
              <div className="barre-actions barre-actions--titre">
                <h2>Derniers imports</h2>
                <Link className="bouton bouton--lien" to="/imports">
                  Ouvrir les imports
                </Link>
              </div>
              {tableau.data.imports.length === 0 ? (
                <p className="alerte alerte--info" role="status">
                  Aucun lot importé pour l&apos;instant.
                </p>
              ) : (
                <table className="tableau">
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      <th scope="col">Déposé le</th>
                      <th scope="col">Statut</th>
                      <th scope="col">Anomalies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableau.data.imports.map((lot) => (
                      <tr key={lot.id}>
                        <td>{IMPORT_TYPE_LABELS[lot.type]}</td>
                        <td>{formaterHorodatage(lot.createdAt)}</td>
                        <td>
                          <span
                            className={
                              lot.status === 'DONE'
                                ? 'etiquette etiquette--actif'
                                : 'etiquette etiquette--attente'
                            }
                          >
                            {IMPORT_STATUS_LABELS[lot.status]}
                          </span>
                        </td>
                        <td className="droite">
                          {lot.missingKeys > 0 ? (
                            <span className="nombre nombre--alerte">
                              {nombre(lot.missingKeys)} clé(s) manquante(s)
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>

          {tableau.data.historique.length > 1 && (
            <section className="carte">
              <h2>Relevés du KPI J+{ANCIENNETE_ALERTE_JOURS}</h2>
              <p className="sous-titre">
                Une mesure est relevée à chaque recalcul d&apos;écarts, c&apos;est-à-dire après
                chaque import. La tendance se lit sur des relevés réels, jamais sur une
                reconstitution.
              </p>
              <table className="tableau">
                <thead>
                  <tr>
                    <th scope="col">Relevé le</th>
                    <th scope="col">Données jusqu&apos;au</th>
                    <th scope="col">Écarts ouverts</th>
                    <th scope="col">Au-delà de J+{ANCIENNETE_ALERTE_JOURS}</th>
                    <th scope="col">Familles ouvertes</th>
                  </tr>
                </thead>
                <tbody>
                  {[...tableau.data.historique].reverse().map((mesure) => (
                    <tr key={mesure.mesureLe}>
                      <td>{formaterHorodatage(mesure.mesureLe)}</td>
                      <td>{mesure.dernierJour ?? '—'}</td>
                      <td className="droite">{nombre(mesure.ecartsOuverts)}</td>
                      <td className="droite">{nombre(mesure.auDelaJ5)}</td>
                      <td className="droite">{nombre(mesure.ecartsFamille)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </>
  );
}
